import React, { useState, useCallback } from 'react';
import {
  FormControl,
  MenuItem,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Select,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { LabeledField } from '@/__core/components/LabeledField';

interface WithAddButtonSelectProps {
  options: Array<{ id: string; name: string; [key: string]: any }>;
  value: string;
  onChange: (event: any, child?: React.ReactNode) => void;
  onBlur?: (event: any) => void;
  placeholder?: string;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  onAddNew?: (newItem: { name: string }) => Promise<{ id: string; name: string }>;
  addDialogTitle?: string;
  addDialogLabel?: string;
  addDialogPlaceholder?: string;
  sx?: any;
  // New props for custom dialog support
  CustomDialog?: React.ComponentType<{
    open: boolean;
    onClose: () => void;
    onSave: (data: any) => Promise<any>;
    loading?: boolean;
  } & Record<string, any>>;
  onAddNewCustom?: (data: any) => Promise<{ id: string; name: string }>;
  customDialogProps?: Record<string, any>;
  // Permission control for '+' button visibility
  canAddNew?: boolean;
  // New prop for custom select component
  CustomSelect?: React.ComponentType<{
    value: string;
    onChange: (event: any) => void;
    onBlur?: (event: any) => void;
    options: Array<{ id: string; name: string; [key: string]: any }>;
    placeholder?: string;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    disabled?: boolean;
    required?: boolean;
    sx?: any;
  }>;
  showColor?: boolean;
}

function withAddButtonSelect<P extends object>(
  WrappedComponent: React.ComponentType<P>
) {
  void WrappedComponent;

  return React.forwardRef<HTMLDivElement, P & WithAddButtonSelectProps>(
    (props, ref) => {
      const {
        options,
        value,
        onChange,
        onBlur,
        placeholder = "Chọn...",
        label,
        size = "small",
        fullWidth = true,
        disabled = false,
        required = false,
        onAddNew,
        addDialogTitle = "Thêm mới",
        addDialogLabel = "Tên",
        addDialogPlaceholder = "Nhập tên...",
        sx,
        CustomDialog,
        onAddNewCustom,
        customDialogProps,
        canAddNew = true,
        CustomSelect,
        showColor = false,
        ...restProps
      } = props;

      const [open, setOpen] = useState(false);
      const [newItemName, setNewItemName] = useState('');
      const [loading, setLoading] = useState(false);
      const [error, setError] = useState<string | null>(null);
      const hasAddButton = Boolean(onAddNew || (CustomDialog && onAddNewCustom));
      const showAddButton = canAddNew && hasAddButton;
      const normalizedValue = typeof value === 'string' ? value : String(value || '');
      const matchedValue = normalizedValue && options && options.length > 0
        ? options.find((opt) => String(opt.id) === normalizedValue)
        : undefined;

      const handleAddNew = useCallback(async () => {
        if (!newItemName.trim() || !onAddNew) return;

        try {
          setLoading(true);
          setError(null);
          const newItem = await onAddNew({ name: newItemName.trim() });

          onChange({ target: { value: newItem.id } });

          setNewItemName('');
          setOpen(false);
        } catch (error: any) {
          setError(error.message || 'Có lỗi xảy ra khi tạo mới');
        } finally {
          setLoading(false);
        }
      }, [newItemName, onAddNew, options, onChange]);

      const handleClose = useCallback(() => {
        setOpen(false);
        setNewItemName('');
        setError(null);
      }, []);

      const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          handleAddNew();
        }
      }, [handleAddNew]);

      const renderOptionLabel = (option: { id: string; name: string; [key: string]: any }) => {
        if (!showColor || !option.color) {
          return option.name;
        }

        return (
          <Typography variant='body2' sx={{ color: option.color, fontWeight: 600 }}>
            {option.name}
          </Typography>
        );
      };

      return (
        <>
          <Box
            className={`Select__with-add-button${showAddButton ? ' Select__with-add-button--has-icon' : ' Select__inline-icon'}`}
            sx={{ width: fullWidth ? '100%' : 'auto' }}
          >
            {CustomSelect ? (
              <CustomSelect
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                options={options}
                placeholder={placeholder}
                size={size}
                fullWidth={fullWidth}
                disabled={disabled}
                required={required}
                sx={sx}
              />
            ) : (
              <FormControl fullWidth={fullWidth} size={size}>
                <Select
                  ref={ref}
                  value={matchedValue ? normalizedValue : ""}
                  onChange={(event) => {
                    // Ensure we have a proper change event with target.value
                    const changeEvent = {
                      ...event,
                      target: {
                        ...event.target,
                        value: event.target.value
                      }
                    };
                    onChange(changeEvent);
                  }}
                  onBlur={onBlur}
                  displayEmpty
                  size={size}
                  fullWidth={fullWidth}
                  disabled={disabled}
                  required={required}
                  renderValue={(selected: any) => {
                    const selectedValue = String(selected || '');
                    if (!selectedValue) {
                      return <em>{placeholder}</em>;
                    }

                    const selectedOption = options.find((opt) => String(opt.id) === selectedValue);
                    if (!selectedOption) {
                      return selectedValue;
                    }

                    if (!showColor || !selectedOption.color) {
                      return selectedOption.name;
                    }

                    return renderOptionLabel(selectedOption);
                  }}
                  sx={{
                    width: '100%',
                    ...sx,
                  }}
                  {...restProps}
                >
                  <MenuItem value="" disabled>
                    <em>{placeholder}</em>
                  </MenuItem>

                  {options && options.length > 0 ? options.map((option) => (
                    <MenuItem key={option.id} value={String(option.id)}>
                      {renderOptionLabel(option)}
                    </MenuItem>
                  )) : null}
                </Select>
              </FormControl>
            )}
            
            {showAddButton && (
              <IconButton
                className="Select__with-add-button-icon"
                onClick={() => setOpen(true)}
                disabled={disabled}
                sx={{
                  position: 'absolute',
                  color: '#000000',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '&:disabled': {
                    color: '#d1d5db',
                  },
                  width: '20px',
                  height: '20px',
                  zIndex: 1,
                }}
                size="small"
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </Box>

          {/* Add New Dialog */}
          {CustomDialog && onAddNewCustom ? (
            <CustomDialog
              open={open}
              onClose={handleClose}
              onSave={async (data) => {
                try {
                  setLoading(true);
                  setError(null);
                  const newItem = await onAddNewCustom(data);

                  if (newItem && newItem.id) {
                    onChange({ target: { value: newItem.id } });
                  }

                  setOpen(false);
                  return newItem;
                } catch (error: any) {
                  console.error('withAddButtonSelect: Error in onSave:', error);
                  setError(error.message || 'Có lỗi xảy ra khi tạo mới');
                  throw error;
                } finally {
                  setLoading(false);
                }
              }}
              loading={loading}
              {...customDialogProps}
            />
          ) : (
            <Dialog
              open={open}
              onClose={handleClose}
              maxWidth='sm'
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: '12px',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <DialogTitle
                sx={{
                  borderBottom: '1px solid #e5e7eb',
                  pb: 2,
                  '& .MuiTypography-root': {
                    color: '#111827',
                  },
                }}
              >
                {addDialogTitle}
              </DialogTitle>

              <DialogContent sx={{ mt: 3 }}>
                {error && (
                  <Box sx={{ mb: 2 }}>
                    <Typography color='error' variant='body2'>
                      {error}
                    </Typography>
                  </Box>
                )}

                <LabeledField label={addDialogLabel} required>
                  <TextField
                    fullWidth
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder={addDialogPlaceholder}
                    autoFocus
                    onKeyPress={handleKeyPress}
                    size='small'
                  />
                </LabeledField>
              </DialogContent>

              <DialogActions
                sx={{
                  borderTop: '1px solid #e5e7eb',
                  p: 2,
                  gap: 2,
                }}
              >
                <Button
                  onClick={handleClose}
                  variant='outlined'
                  disabled={loading}
                  sx={{
                    borderColor: '#d1d5db',
                    color: '#6b7280',
                    '&:hover': {
                      borderColor: '#9ca3af',
                      backgroundColor: '#f9fafb',
                    },
                  }}
                >
                  Hủy
                </Button>
                <Button
                  onClick={handleAddNew}
                  variant='contained'
                  disabled={!newItemName.trim() || loading}
                  startIcon={loading ? <CircularProgress size={16} /> : null}
                  sx={{
                    backgroundColor: '#10b981',
                    '&:hover': {
                      backgroundColor: '#059669',
                    },
                    '&:disabled': {
                      backgroundColor: '#d1d5db',
                      color: '#9ca3af',
                    },
                  }}
                >
                  {loading ? 'Đang tạo...' : 'Tạo mới'}
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </>
      );
    }
  );
}

export default withAddButtonSelect;
